import React, { useState, useEffect, useCallback } from 'react';
import { Text } from '@cuemath/web-comps';
import { usePatchAgentDetails, invalidateAgents } from 'actions/support-agent';
import {
  AGENT_ROLES,
  ROLE_LABELS,
  AGENT_ACTIONS,
  CACHE_KEYS,
  ERROR_AUTO_CLEAR_MS,
} from '../../constants';
import { FormField, FormLabel } from './styled';
import RolePicker from './role-picker';
import {
  ModalOverlay,
  ModalCard,
  ModalHead,
  ModalBody,
  ModalFoot,
  BtnPrimary,
  BtnSecondary,
  IconBtn,
  ModalErrorMsg,
} from '../../styled-comps';
import Icon from '../icon';

const ChangeRoleModal = ({ agent, onClose }) => {
  const [role, setRole] = useState(AGENT_ROLES.AGENT);
  const [submitError, setSubmitError] = useState(null);

  const { patch: patchAgent, isProcessing } = usePatchAgentDetails({
    onComplete: error => {
      if (error) {
        setSubmitError(typeof error === 'string' ? error : 'Failed to update role');
        return;
      }
      invalidateAgents(CACHE_KEYS.AGENTS);
      onClose();
    },
  });

  useEffect(() => {
    if (!agent) return;
    setRole(agent.role || AGENT_ROLES.AGENT);
    setSubmitError(null);
  }, [agent]);

  useEffect(() => {
    if (!submitError) return undefined;
    const t = setTimeout(() => setSubmitError(null), ERROR_AUTO_CLEAR_MS);
    return () => clearTimeout(t);
  }, [submitError]);

  const handleSubmit = useCallback(() => {
    if (!agent || isProcessing) return;
    patchAgent(agent.user_id, { action: AGENT_ACTIONS.UPDATE_ROLE, role });
  }, [agent, isProcessing, role, patchAgent]);

  if (!agent) return null;

  const unchanged = role === agent.role;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalCard onClick={e => e.stopPropagation()}>
        <ModalHead>
          <Text variant="body1Medium">Change role · {agent.name}</Text>
          <IconBtn onClick={onClose}>
            <Icon name="close" size={16} />
          </IconBtn>
        </ModalHead>
        <ModalBody $minHeight={280}>
          {submitError && <ModalErrorMsg>{submitError}</ModalErrorMsg>}
          <FormField>
            <FormLabel>Role</FormLabel>
            <RolePicker
              value={role}
              onChange={setRole}
              options={[
                { value: AGENT_ROLES.AGENT, label: ROLE_LABELS.agent },
                { value: AGENT_ROLES.LEAD, label: ROLE_LABELS.lead },
                { value: AGENT_ROLES.ADMIN, label: ROLE_LABELS.admin },
              ]}
            />
          </FormField>
        </ModalBody>
        <ModalFoot>
          <BtnSecondary onClick={onClose}>Cancel</BtnSecondary>
          <BtnPrimary onClick={handleSubmit} disabled={unchanged || isProcessing}>
            {isProcessing ? 'Saving...' : 'Save'}
          </BtnPrimary>
        </ModalFoot>
      </ModalCard>
    </ModalOverlay>
  );
};

export default ChangeRoleModal;
